<script lang="ts">
  import type { DerivedWorkout, Lap } from "../../lib/lapAnalysis";
  import { fmtPaceTick } from "../../lib/format";
  import StrokePill from "../../lib/StrokePill.svelte";

  interface Props { dw: DerivedWorkout }
  let { dw }: Props = $props();

  type Col = { key: keyof Lap; label: string; fmt: (v: number) => string; lowerBetter: boolean };
  const cols: Col[] = [
    { key: "durationSeconds", label: "Time", fmt: (v) => `${Math.floor(v)}s`, lowerBetter: true },
    { key: "spl", label: "SPL", fmt: (v) => v.toFixed(0), lowerBetter: true },
    { key: "swolf", label: "SWOLF", fmt: (v) => v.toFixed(0), lowerBetter: true },
    { key: "avgHr", label: "Avg HR", fmt: (v) => v.toFixed(0), lowerBetter: false },
    { key: "pacePer100s", label: "Pace", fmt: (v) => fmtPaceTick(v), lowerBetter: true },
  ];

  // Hoist min/max per column outside the inner {#each c} — previously the heatmap
  // re-scanned every cell of every row to recompute these (O(L²·C)). Now one pass
  // per column, regardless of row count.
  let colStats = $derived.by(() => {
    const stats = new Map<keyof Lap, { min: number; max: number }>();
    for (const c of cols) {
      let min = Infinity;
      let max = -Infinity;
      for (const lap of dw.laps) {
        // Heat-scale decision (07-RESEARCH Open Q1, resolved by Plan 07-03): skip
        // excluded laps so one greyed SWOLF-140 poison lap no longer sets the
        // column max and washes out every other cell's contrast. The lap is still
        // RENDERED greyed below — only the scale ignores it.
        if (lap.effectiveExcluded) continue;
        const v = lap[c.key];
        if (typeof v === "number" && Number.isFinite(v)) {
          if (v < min) min = v;
          if (v > max) max = v;
        }
      }
      stats.set(c.key, Number.isFinite(min) ? { min, max } : { min: 0, max: 0 });
    }
    return stats;
  });

  function cellIntensity(value: number | null, key: keyof Lap, lowerBetter: boolean): number {
    if (value === null) return 0;
    const s = colStats.get(key);
    if (!s || s.min === s.max) return s ? 0.5 : 0;
    const norm = (value - s.min) / (s.max - s.min);
    return lowerBetter ? 1 - norm : norm;
  }

  /** Translate intensity [0,1] to a warm-overlay opacity. Higher intensity = hotter. */
  function cellStyle(intensity: number): string {
    if (intensity === 0) return "background: transparent";
    return `background: rgba(255, 107, 107, ${(intensity * 0.55).toFixed(3)})`;
  }
</script>

<article class="proto">
  <header>
    <span class="num">03</span>
    <h2>Lap Heatmap</h2>
    <p class="story">
      Every lap × every metric. Hotter cells = harder relative to the rest of this workout.
      Spot the lap that broke you at a glance.
    </p>
  </header>

  <div class="grid-wrap">
    <table class="grid">
      <thead>
        <tr>
          <th>Lap</th>
          <th>Stroke</th>
          {#each cols as c (c.key)}<th>{c.label}</th>{/each}
        </tr>
      </thead>
      <tbody>
        {#each dw.laps as lap (lap.index)}
          <tr class:peak={lap.index === dw.peakLapIndex} class:excluded={lap.effectiveExcluded}>
            <td class="lapnum">L{lap.index}</td>
            <td>
              <StrokePill stroke={lap.strokeStyle} />
            </td>
            {#each cols as c (c.key)}
              {@const value = lap[c.key] as number | null}
              {@const intensity = cellIntensity(value, c.key, c.lowerBetter)}
              <td class="cell" style={cellStyle(intensity)}>
                {value === null ? "—" : c.fmt(value)}
              </td>
            {/each}
          </tr>
        {/each}
      </tbody>
    </table>
  </div>

  {#if dw.peakLapIndex !== null}
    <div class="callout">
      ⌐ Peak effort: <strong>Lap {dw.peakLapIndex}</strong>
      ({dw.laps[dw.peakLapIndex - 1]?.strokeStyle})
    </div>
  {/if}
</article>

<style>
  .proto { background: var(--bg-card); border-radius: 12px; padding: 1rem; margin: 0.75rem 0; }
  header { margin-bottom: 0.75rem; }
  .num { display: inline-block; padding: 0.1rem 0.5rem; background: var(--accent-yellow); color: var(--bg-canvas); font-size: 0.7rem; font-weight: 700; border-radius: 3px; letter-spacing: 1px; }
  h2 { font-size: 1.1rem; font-weight: 700; margin: 0.4rem 0 0.2rem; }
  .story { font-size: 0.78rem; color: var(--text-muted); margin: 0; }
  .grid-wrap { overflow-x: auto; max-height: 360px; overflow-y: auto; border-radius: 6px; background: var(--bg-canvas); padding: 0.2rem; }
  .grid { width: 100%; border-collapse: collapse; font-size: 0.75rem; font-variant-numeric: tabular-nums; }
  .grid th { position: sticky; top: 0; background: var(--bg-canvas); color: var(--text-muted); font-weight: 500; padding: 0.35rem 0.4rem; text-align: left; font-size: 0.7rem; letter-spacing: 0.5px; text-transform: uppercase; }
  .grid td { padding: 0.3rem 0.4rem; border-bottom: 1px solid rgba(255,255,255,0.04); }
  .lapnum { color: var(--text-muted); font-size: 0.7rem; }
  .cell { font-weight: 500; }
  tr.peak { box-shadow: inset 3px 0 0 var(--accent-fire); }
  tr.peak .lapnum { color: var(--accent-fire); font-weight: 700; }
  /* Quarantine (D-16): greyed-in-place, lap number kept continuous (L7 stays L7).
     Reuses the .peak left-edge inset idiom but GREY not fire — mute, never delete. */
  tr.excluded { box-shadow: inset 3px 0 0 #8F9098; opacity: 0.45; }
  tr.excluded .lapnum { color: #8F9098; }
  .callout { margin-top: 0.75rem; font-size: 0.78rem; color: var(--accent-fire); padding: 0.4rem 0.6rem; background: rgba(255, 87, 34, 0.1); border-radius: 4px; }
  .callout strong { font-weight: 700; }
</style>
